// Copying text to the clipboard, with a fallback for the contexts where the
// async Clipboard API is not available.
//
// navigator.clipboard is gated by the `clipboard-write` Permissions Policy,
// whose default for a cross-origin iframe is "deny unless the frame carries
// allow='clipboard-write'". Chrome 152 started enforcing it (crbug.com/414348233),
// which broke every copy in the app when it runs embedded in the Publicis
// portal (the ?oid=publicis / ?iframe entry point, see the frame-ancestors of
// firebase.json): writeText rejects with NotAllowedError and, where the
// rejection was not handled, surfaced as a reported error.
//
// document.execCommand('copy') is NOT gated by that policy, so the textarea
// path keeps copying alive inside the iframe. It also covers a non-secure
// context, where navigator.clipboard is simply undefined. It does need the
// document to be user-activated, which every caller satisfies: all of them run
// from a click or from the spreadsheet's Ctrl+C.
//
// Resolves to whether the text made it to the clipboard, so a caller only
// claims success when there was one.
export function copyTextToClipboard (text, textarea) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => copyWithTextarea(text, textarea)
    )
  }
  return Promise.resolve(copyWithTextarea(text, textarea))
}

// Selects the text in a textarea and asks the document to copy it. Callers that
// already own a hidden textarea holding the selection (jspreadsheet does) can
// pass it in, so the focus is not taken away from where the user is working;
// otherwise a throwaway one is used and removed.
export function copyWithTextarea (text, textarea) {
  const own = !textarea
  const area = textarea || document.createElement('textarea')
  if (own) {
    area.setAttribute('readonly', '')
    area.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;'
    document.body.appendChild(area)
  }
  area.value = text
  area.select()
  let copied = false
  try {
    copied = document.execCommand('copy')
  } catch (e) {
    copied = false
  }
  if (own) {
    document.body.removeChild(area)
  }
  return copied
}
